/* eslint-disable @typescript-eslint/no-unused-vars */
"use client";

import { Trash2, UserPlus, Search, Filter } from "lucide-react";
import { useState, useMemo } from "react";

import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/_module/components/ui/table";
import { useToast } from "@/app/_module/hooks/use-toast";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import { getAdminAccess } from "@/app/_module/utils/admin-access";

import { 
  useGetAgencies, 
  useDeleteAgency, 
  useUpdateAgency,
  useCreateAgency,
  type AgencyFilters 
} from "../../hooks/use-hook";
import type { Agency } from "../../types/types";
import { allowedAgencyRoles } from "../AgencyStats";

import AddUserToAgencyModal from "./AddUserToAgencyModal";
import ConfirmDeleteModal from "./confirmModal";
import EditAgencyModal from "./editAgency";

interface ColumnDef<T> {
  key: keyof T | "actions" | "status";
  label: string;
  render?: (item: T) => React.ReactNode;
}

const normalizeRole = (role?: string) => (role || "").toLowerCase().replace(/\s+/g, "_");

const AgencyManagement = function() {
  const { toast } = useToast();
  const [resourceToDelete, setResourceToDelete] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");
  const { user } = useUserContext();

  // Determine admin access level for proper scoping
  const agencyRole = user.currentAgency?.role;
  const adminAccess = getAdminAccess(user.user.userType, agencyRole);

  // Extract stable primitive values for dependencies
  const currentAgencyId = user.currentAgency?.agency?.id;
  const currentAgencyRole = user.currentAgency?.role;

  // Fetch agencies with filters
  const { data: agenciesResponse, isLoading, error, refetch } = useGetAgencies();

  // Mutations
  const deleteAgencyMutation = useDeleteAgency();
  const updateAgencyMutation = useUpdateAgency();
  const createAgencyMutation = useCreateAgency();

  // Filter agencies based on admin access level and apply search/status filters
  const filteredAgencies = useMemo(() => {
    if (!agenciesResponse?.items) return [];
    let filtered = agenciesResponse.items;

    // Apply role-based scoping
    if (adminAccess.canManageAllAgencies) {
      // System Admin can see all agencies
      filtered = agenciesResponse.items;
    } else if (adminAccess.canManageOwnAgencies && currentAgencyId) {
      // Agency Owner/Admin can only see agencies they own or are admin of
      filtered = agenciesResponse.items.filter((agency: Agency) => {
        const role = normalizeRole(currentAgencyRole);
        return allowedAgencyRoles.has(role) && currentAgencyId === agency.id;
      });
    } else {
      // Regular users cannot manage agencies
      filtered = [];
    }

    // Filter by status
    if (statusFilter !== "all") {
      const isActive = statusFilter === "active";
      filtered = filtered.filter((agency: Agency) => agency.isActive === isActive);
    }

    // Filter by search term with improved type safety
    if (searchTerm) {
      filtered = filtered.filter((agency: Agency) =>
        agency.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agency.contactEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agency.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  }, [agenciesResponse?.items, currentAgencyId, currentAgencyRole, adminAccess, statusFilter, searchTerm]);

  const handleDeleteConfirm = async () => {
    if (resourceToDelete) {
      try {
        await deleteAgencyMutation.mutateAsync(resourceToDelete);
      } catch {
        toast({ title: "Error", description: "Failed to delete agency", variant: "destructive" });
      }
      setResourceToDelete(null);
    }
  };

  const handleEdit = async (data: Agency, close: () => void) => {
    try {
      await updateAgencyMutation.mutateAsync({
        agencyId: data.id,
        data: {
          name: data.name,
          description: data.description,
          contactEmail: data.contactEmail,
          isActive: data.isActive,
        }
      });
      close();
    } catch (error) {

    }
  };

  const handleCreate = async (data: Agency, reset: () => void, close: () => void) => {
    try {
      await createAgencyMutation.mutateAsync({
        name: data.name,
        description: data.description,
        contactEmail: data.contactEmail,
        isActive: data.isActive,
      });
      reset();
      close();
    } catch (error) {

    }
  };

  const columns: ColumnDef<Agency>[] = [
    { key: "name", label: "Agency Name" },
    { key: "description", label: "Description" },
    { key: "contactEmail", label: "Contact Email" },
    { 
      key: "status", 
      label: "Status",
      render: (item) => (
        <Badge variant={item.isActive ? "default" : "secondary"}>
          {item.isActive ? "Active" : "Inactive"}
        </Badge>
      )
    },
    {
      key: "actions",
      label: "Actions",
      render: (item) => (
        <div className="flex gap-1 items-center">
          <EditAgencyModal
            defaultValues={item}
            onEdit={handleEdit}
          />
        </div>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p>Loading agencies...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <p className="text-destructive">Failed to load agencies</p>
          <Button
            className="mt-2"
            onClick={() => refetch()}
            variant="outline"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // Check if user has permission to manage agencies
  if (!adminAccess.canManageOwnAgencies) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <p className="text-muted-foreground">
            You don't have permission to manage agencies
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-card">
        <div className="agency-mgmt-head flex pt-4 items-center justify-between p-4">
          <h2 className="text-xl font-semibold">Manage Agencies ({filteredAgencies.length})</h2>

          {/* Search and Filter Controls */}
          <div className="flex gap-4 items-center">
            {/* Search */}
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                className="pl-10"
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search agencies..."
                value={searchTerm}
              />
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <Select onValueChange={(value) => setStatusFilter(value as "all" | "active" | "inactive")} value={statusFilter}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

          </div>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((col) => (
                <TableHead key={col.key.toString()}>{col.label}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredAgencies.length === 0 ? (
              <TableRow>
                <TableCell className="text-center py-8 text-muted-foreground" colSpan={columns.length}>
                  {searchTerm || statusFilter !== "all"
                    ? "No agencies match your filters"
                    : "No agencies found"}
                </TableCell>
              </TableRow>
            ) : (
              filteredAgencies.map((agency) => (
                <TableRow key={agency.id}>
                  {columns.map((col) => (
                    <TableCell key={col.key.toString()}>
                      {col.key === "actions" || col.key === "status"
                        ? col.render?.(agency)
                        : String(agency[col.key] || '')}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <ConfirmDeleteModal
        onConfirm={handleDeleteConfirm}
        onOpenChange={(open) => !open && setResourceToDelete(null)}
        open={!!resourceToDelete}
      />
    </div>
  );
};

export default AgencyManagement;
